import { useState, useEffect, useRef, useCallback } from 'react';

// ─── Kalman filter (1D) ───────────────────────────────────────────────────────
class KalmanFilter1D {
    constructor(processNoise = 0.0005) {
        this.Q = processNoise;   // lower = smoother but more lag
        this.R = 25;
        this.x = null;
        this.P = 1;
    }

    update(measurement, accuracyMeters) {
        // Scale measurement noise by GPS accuracy squared
        this.R = Math.max(1, accuracyMeters * accuracyMeters * 0.0008);

        if (this.x === null) {
            this.x = measurement;
            this.P = this.R;
            return measurement;
        }

        this.P = this.P + this.Q;
        const K = this.P / (this.P + this.R);
        this.x = this.x + K * (measurement - this.x);
        this.P = (1 - K) * this.P;
        return this.x;
    }

    reset() { this.x = null; this.P = 1; }
}

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_ACCURACY_FOR_UPDATE  = 80;  // metres — ignore noisier readings
const MAX_ACCURACY_CIRCLE_RADIUS = 40; // metres — cap visual circle
const MIN_MOVEMENT_FOR_HEADING  = 8;  // metres — don't update cone on tiny wobbles
const CONE_DISTANCE = 50;             // metres — length of direction beam
const CONE_ANGLE    = 30;             // degrees — narrower = more like Google Maps

// ─── Hook ─────────────────────────────────────────────────────────────────────
const useMapWithLocation = ({
    enableTracking     = true,
    minDistanceChange  = 5,
    showAccuracyCircle = true,
    highAccuracy       = true,
} = {}) => {

    const [userLocation,     setUserLocation]     = useState(null);
    const [locationError,    setLocationError]    = useState(null);
    const [locationErrorCode,setLocationErrorCode]= useState(null);
    const [isLoadingLocation,setIsLoadingLocation]= useState(true);

    const userMarkerRef    = useRef(null);
    const pulseMarkerRef   = useRef(null); // outer pulse ring (OverlayView or second marker)
    const accuracyCircleRef= useRef(null);
    const directionConeRef = useRef(null);
    const watchIdRef       = useRef(null);
    const lastPositionRef  = useRef(null);
    const headingRef       = useRef(null);

    const kalmanLatRef = useRef(new KalmanFilter1D(0.0005));
    const kalmanLngRef = useRef(new KalmanFilter1D(0.0005));

    // ── Distance helpers ──
    const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
        const R = 6371e3;
        const φ1 = lat1 * Math.PI / 180;
        const φ2 = lat2 * Math.PI / 180;
        const Δφ = (lat2 - lat1) * Math.PI / 180;
        const Δλ = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(Δφ/2)**2 + Math.cos(φ1)*Math.cos(φ2)*Math.sin(Δλ/2)**2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    }, []);

    const formatDistance = useCallback((m) => {
        return m < 1000 ? `${Math.round(m)}m` : `${(m/1000).toFixed(1)}km`;
    }, []);

    // ── Build the direction cone path ──
    const buildConePath = useCallback((lat, lng, heading) => {
        const leftAngle  = (heading - CONE_ANGLE / 2) * Math.PI / 180;
        const rightAngle = (heading + CONE_ANGLE / 2) * Math.PI / 180;
        const headRad    = heading * Math.PI / 180;
        const mLat = CONE_DISTANCE / 111320;
        const mLng = CONE_DISTANCE / (111320 * Math.cos(lat * Math.PI / 180));

        return [
            { lat, lng },
            { lat: lat + Math.cos(leftAngle)  * mLat, lng: lng + Math.sin(leftAngle)  * mLng },
            { lat: lat + Math.cos(headRad)    * mLat, lng: lng + Math.sin(headRad)    * mLng },
            { lat: lat + Math.cos(rightAngle) * mLat, lng: lng + Math.sin(rightAngle) * mLng },
        ];
    }, []);

    // ── Process each GPS update ──
    const processLocationUpdate = useCallback((position) => {
        const rawLat  = position.coords.latitude;
        const rawLng  = position.coords.longitude;
        const accuracy= position.coords.accuracy;

        // Gate: ignore very inaccurate readings — don't move the dot
        if (accuracy > MAX_ACCURACY_FOR_UPDATE) return;

        const smoothedLat = kalmanLatRef.current.update(rawLat, accuracy);
        const smoothedLng = kalmanLngRef.current.update(rawLng, accuracy);

        // ── Heading ──
        let heading = position.coords.heading;

        // Compute from movement if device doesn't give us one
        if ((heading === null || heading === undefined || isNaN(heading)) && lastPositionRef.current) {
            const moved = calculateDistance(
                lastPositionRef.current.lat, lastPositionRef.current.lng,
                smoothedLat, smoothedLng
            );
            if (moved >= MIN_MOVEMENT_FOR_HEADING) {
                const dLon = (smoothedLng - lastPositionRef.current.lng) * Math.PI / 180;
                const lat1r = lastPositionRef.current.lat * Math.PI / 180;
                const lat2r = smoothedLat * Math.PI / 180;
                const y = Math.sin(dLon) * Math.cos(lat2r);
                const x = Math.cos(lat1r) * Math.sin(lat2r) - Math.sin(lat1r) * Math.cos(lat2r) * Math.cos(dLon);
                heading = (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
                headingRef.current = heading;
            } else {
                // Not enough movement — keep last known heading, don't spin the cone
                heading = headingRef.current;
            }
        } else if (heading !== null && heading !== undefined && !isNaN(heading)) {
            headingRef.current = heading;
        }

        const pos = { lat: smoothedLat, lng: smoothedLng };

        // Update Google Maps objects directly (no setState — avoids React re-render lag)
        if (userMarkerRef.current) {
            userMarkerRef.current.setPosition(pos);
        }

        // Accuracy circle — capped visually, hidden if too inaccurate
        if (accuracyCircleRef.current) {
            const visualRadius = Math.min(accuracy, MAX_ACCURACY_CIRCLE_RADIUS);
            accuracyCircleRef.current.setCenter(pos);
            accuracyCircleRef.current.setRadius(visualRadius);
        }

        // Direction cone — only update when heading is meaningful
        if (directionConeRef.current && headingRef.current !== null && headingRef.current !== undefined) {
            directionConeRef.current.setPath(buildConePath(smoothedLat, smoothedLng, headingRef.current));
        }

        // React state — throttled by minDistanceChange
        const shouldUpdateState = !lastPositionRef.current ||
            calculateDistance(
                lastPositionRef.current.lat, lastPositionRef.current.lng,
                smoothedLat, smoothedLng
            ) >= minDistanceChange;

        if (shouldUpdateState) {
            const newLocation = {
                lat: smoothedLat,
                lng: smoothedLng,
                accuracy,
                timestamp: position.timestamp,
                heading: headingRef.current,
            };
            setUserLocation(newLocation);
            setLocationError(null);
            setLocationErrorCode(null);
            lastPositionRef.current = newLocation;
        }
    }, [minDistanceChange, calculateDistance, buildConePath]);

    // ── GPS tracking lifecycle ──
    useEffect(() => {
        if (!enableTracking) { setIsLoadingLocation(false); return; }

        if (!navigator.geolocation) {
            setLocationError('Geolocation not supported');
            setLocationErrorCode(0);
            setIsLoadingLocation(false);
            return;
        }

        const startTracking = () => {
            kalmanLatRef.current.reset();
            kalmanLngRef.current.reset();

            // Fast first fix — low accuracy, high maximumAge
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const accuracy = position.coords.accuracy;
                    const lat = kalmanLatRef.current.update(position.coords.latitude, accuracy);
                    const lng = kalmanLngRef.current.update(position.coords.longitude, accuracy);

                    const location = {
                        lat, lng, accuracy,
                        timestamp: position.timestamp,
                        heading: position.coords.heading,
                    };
                    setUserLocation(location);
                    setIsLoadingLocation(false);
                    setLocationError(null);
                    setLocationErrorCode(null);
                    lastPositionRef.current = location;
                },
                () => { setIsLoadingLocation(false); },
                { enableHighAccuracy: false, timeout: 5000, maximumAge: 30000 }
            );

            // High-accuracy continuous watch
            watchIdRef.current = navigator.geolocation.watchPosition(
                processLocationUpdate,
                (err) => {
                    setLocationError(err.message);
                    setLocationErrorCode(err.code);
                },
                { enableHighAccuracy: highAccuracy, timeout: 10000, maximumAge: 0 }
            );
        };

        if (navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' }).then((result) => {
                if (result.state === 'denied') {
                    setLocationError('Location access denied');
                    setLocationErrorCode(1);
                    setIsLoadingLocation(false);
                    return;
                }
                startTracking();
                result.onchange = () => {
                    if (result.state === 'denied') {
                        setLocationError('Location access denied');
                        setLocationErrorCode(1);
                        if (watchIdRef.current) {
                            navigator.geolocation.clearWatch(watchIdRef.current);
                            watchIdRef.current = null;
                        }
                    }
                };
            });
        } else {
            startTracking();
        }

        return () => {
            if (watchIdRef.current) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
        };
    }, [enableTracking, highAccuracy, processLocationUpdate]);

    // ── Mount user marker + cone + circle on a Google Map ──
    const updateUserMarkerOnMap = useCallback((map) => {
        if (!map || !userLocation) return;

        const pos = { lat: userLocation.lat, lng: userLocation.lng };
        const heading = userLocation.heading ?? headingRef.current ?? 0;

        // ── User dot — white ring + blue fill, looks like Google Maps ──
        if (!userMarkerRef.current) {
            userMarkerRef.current = new window.google.maps.Marker({
                position: pos,
                map,
                title: 'Your location',
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: '#4285F4',
                    fillOpacity: 1,
                    strokeColor: '#FFFFFF',
                    strokeWeight: 3,
                    scale: 9,
                },
                zIndex: 1001,
                optimized: true,
            });

            // Outer pulse ring — second marker, larger, low opacity
            pulseMarkerRef.current = new window.google.maps.Marker({
                position: pos,
                map,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: '#4285F4',
                    fillOpacity: 0.18,
                    strokeColor: '#4285F4',
                    strokeOpacity: 0.35,
                    strokeWeight: 1.5,
                    scale: 18,
                },
                zIndex: 999,
                optimized: true,
                clickable: false,
            });
        } else {
            // Keep pulse ring in sync
            if (pulseMarkerRef.current) pulseMarkerRef.current.setPosition(pos);
        }

        // ── Direction cone — narrow beam, very light ──
        if (!directionConeRef.current && heading !== null && heading !== undefined) {
            directionConeRef.current = new window.google.maps.Polygon({
                map,
                paths: buildConePath(userLocation.lat, userLocation.lng, heading),
                fillColor: '#4285F4',
                fillOpacity: 0.15,
                strokeColor: '#4285F4',
                strokeOpacity: 0.0,   // no outline — just the soft fill
                strokeWeight: 0,
                clickable: false,
                zIndex: 998,
            });
        }

        // ── Accuracy circle — soft, capped ──
        if (!accuracyCircleRef.current && showAccuracyCircle && userLocation.accuracy) {
            const visualRadius = Math.min(userLocation.accuracy, MAX_ACCURACY_CIRCLE_RADIUS);
            accuracyCircleRef.current = new window.google.maps.Circle({
                map,
                center: pos,
                radius: visualRadius,
                fillColor: '#4285F4',
                fillOpacity: 0.08,
                strokeColor: '#4285F4',
                strokeOpacity: 0.2,
                strokeWeight: 1,
                clickable: false,
            });
        }
    }, [userLocation, showAccuracyCircle, buildConePath]);

    // ── Cleanup ──
    const cleanupMarkers = useCallback(() => {
        [userMarkerRef, pulseMarkerRef, accuracyCircleRef, directionConeRef].forEach(ref => {
            if (ref.current) {
                ref.current.setMap(null);
                ref.current = null;
            }
        });
    }, []);

    // ── Utility ──
    const getDistanceToPoint = useCallback((lat, lng) => {
        if (!userLocation) return null;
        return calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
    }, [userLocation, calculateDistance]);

    const isWithinRadius = useCallback((lat, lng, radiusInMeters) => {
        const d = getDistanceToPoint(lat, lng);
        return d !== null && d <= radiusInMeters;
    }, [getDistanceToPoint]);

    const getMapConfig = useCallback((center, zoom = 16) => ({
        center: center || (userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : { lat: 32.0853, lng: 34.7818 }),
        zoom,
        mapTypeId: window.google?.maps?.MapTypeId?.ROADMAP,
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        gestureHandling: 'greedy',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
    }), [userLocation]);

    return {
        userLocation,
        locationError,
        locationErrorCode,
        isLoadingLocation,
        updateUserMarkerOnMap,
        cleanupMarkers,
        getMapConfig,
        calculateDistance,
        formatDistance,
        getDistanceToPoint,
        isWithinRadius,
        userMarkerRef,
        accuracyCircleRef,
        directionConeRef,
    };
};

export default useMapWithLocation;